import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Slide,
  Typography,
  Box,
  Chip,
  Autocomplete,
  Tooltip,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";

interface CustomerSendEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (data: EmailFormData) => void;
  initialRecipient?: string;
  templateOptions?: EmailTemplate[];
  contacts?: Contact[];
}

interface EmailFormData {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  message: string;
  priority: "high" | "normal" | "low";
  scheduledDate?: Date | null;
  attachments: File[];
  trackOpens: boolean;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
}

interface Contact {
  email: string;
  name: string;
  avatar?: string;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomerSendEmailModal: React.FC<CustomerSendEmailModalProps> = ({
  open,
  onClose,
  onSend,
  initialRecipient = "",
  templateOptions = [],
  contacts = [],
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<EmailFormData>({
    to: initialRecipient ? [initialRecipient] : [],
    cc: [],
    bcc: [],
    subject: "",
    message: "",
    priority: "normal",
    scheduledDate: null,
    attachments: [],
    trackOpens: false,
  });

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  const handleChange =
    (field: keyof EmailFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  const handleSubmit = () => {
    onSend(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      to: [],
      cc: [],
      bcc: [],
      subject: "",
      message: "",
      priority: "normal",
      scheduledDate: null,
      attachments: [],
      trackOpens: false,
    });
    setShowCc(false);
    setShowBcc(false);
    setShowSchedule(false);
    setSelectedTemplate(null);
    onClose();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles],
      }));
    }
  };

  const removeAttachment = (index: number) => {
    const updatedFiles = [...formData.attachments];
    updatedFiles.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      attachments: updatedFiles,
    }));
  };

  const applyTemplate = (template: EmailTemplate | null) => {
    if (template) {
      setFormData((prev) => ({
        ...prev,
        subject: template.subject,
        message: template.body,
      }));
    }
    setSelectedTemplate(template);
  };

  const setPriority = (priority: "high" | "normal" | "low") => {
    setFormData((prev) => ({
      ...prev,
      priority,
    }));
  };

  const toggleTrackOpens = () => {
    setFormData((prev) => ({
      ...prev,
      trackOpens: !prev.trackOpens,
    }));
  };

  const scheduleEmail = (date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      scheduledDate: date,
    }));
  };

  const priorityColors = {
    high: theme.palette.error.main,
    normal: theme.palette.primary.main,
    low: theme.palette.text.secondary,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      transitionDuration={500}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <motion.div
              initial={{ rotate: -10, scale: 0.9 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Typography variant="h6" component="span" sx={{ mr: 1 }}>
                New Message
              </Typography>
            </motion.div>
            <Chip
              size="small"
              label={formData.priority}
              sx={{
                backgroundColor: priorityColors[formData.priority],
                color: "#fff",
                ml: 1,
                textTransform: "capitalize",
              }}
            />
          </Box>
          <Box>
            <Autocomplete
              id="email-template"
              options={templateOptions}
              getOptionLabel={(option) => option.name}
              onChange={(_, value) => applyTemplate(value)}
              value={selectedTemplate}
              renderInput={(params) => (
                <TextField {...params} label="Template" size="small" variant="outlined" />
              )}
              sx={{ width: 200, display: "inline-block", mr: 2 }}
            />
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Autocomplete
              multiple
              id="to-input"
              options={contacts.map((c) => c.email)}
              value={formData.to}
              onChange={(_, value) => setFormData({ ...formData, to: value })}
              renderInput={(params) => (
                <TextField {...params} label="To" fullWidth margin="normal" variant="outlined" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const contact = contacts.find((c) => c.email === option);
                  return (
                    <Chip
                      key={option}
                      avatar={contact?.avatar ? <Avatar src={contact.avatar} /> : null}
                      label={contact?.name || option}
                      {...getTagProps({ index })}
                    />
                  );
                })
              }
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button size="small" onClick={() => setShowCc(!showCc)} sx={{ mr: 1 }}>
                {showCc ? "Hide CC" : "Show CC"}
              </Button>
              <Button size="small" onClick={() => setShowBcc(!showBcc)}>
                {showBcc ? "Hide BCC" : "Show BCC"}
              </Button>
            </Box>

            {showCc && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Autocomplete
                  multiple
                  id="cc-input"
                  options={contacts.map((c) => c.email)}
                  value={formData.cc}
                  onChange={(_, value) => setFormData({ ...formData, cc: value })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CC"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                    />
                  )}
                />
              </motion.div>
            )}

            {showBcc && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Autocomplete
                  multiple
                  id="bcc-input"
                  options={contacts.map((c) => c.email)}
                  value={formData.bcc}
                  onChange={(_, value) => setFormData({ ...formData, bcc: value })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BCC"
                      fullWidth
                      margin="normal"
                      variant="outlined"
                    />
                  )}
                />
              </motion.div>
            )}

            <TextField
              label="Subject"
              value={formData.subject}
              onChange={handleChange("subject")}
              fullWidth
              margin="normal"
              variant="outlined"
            />

            <Box sx={{ my: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  p: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Tooltip title="Bold">
                  <IconButton size="small">
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                  <IconButton size="small">
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Underline">
                  <IconButton size="small">
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Tooltip title="Bullet List">
                  <IconButton size="small">
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Insert Link">
                  <IconButton size="small">
                    <InsertLinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <TextField
              label="Message"
              value={formData.message}
              onChange={handleChange("message")}
              fullWidth
              multiline
              minRows={6}
              margin="normal"
              variant="outlined"
            />

            {formData.attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Attachments ({formData.attachments.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {formData.attachments.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                      onDelete={() => removeAttachment(index)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />

            {showSchedule && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Schedule Delivery
                  </Typography>
                  <TextField
                    type="datetime-local"
                    fullWidth
                    size="small"
                    onChange={(e) =>
                      scheduleEmail(e.target.value ? new Date(e.target.value) : null)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </motion.div>
            )}
          </motion.div>
        </DialogContent>

        <DialogActions sx={{ p: 2, flexWrap: "wrap", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Attach Files">
              <IconButton onClick={() => fileInputRef.current?.click()}>
                <AttachFileIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Schedule Email">
              <IconButton
                color={showSchedule ? "primary" : "default"}
                onClick={() => setShowSchedule(!showSchedule)}
              >
                <ScheduleSendIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ mx: 1 }}>
              <Tooltip title="Priority">
                <Box>
                  <Chip
                    label="High"
                    size="small"
                    onClick={() => setPriority("high")}
                    sx={{
                      mr: 0.5,
                      backgroundColor: formData.priority === "high" ? "error.main" : "default",
                      color: formData.priority === "high" ? "white" : "text.secondary",
                    }}
                  />
                  <Chip
                    label="Normal"
                    size="small"
                    onClick={() => setPriority("normal")}
                    sx={{
                      mr: 0.5,
                      backgroundColor: formData.priority === "normal" ? "primary.main" : "default",
                      color: formData.priority === "normal" ? "white" : "text.secondary",
                    }}
                  />
                  <Chip
                    label="Low"
                    size="small"
                    onClick={() => setPriority("low")}
                    sx={{
                      backgroundColor: formData.priority === "low" ? "text.secondary" : "default",
                      color: formData.priority === "low" ? "white" : "text.secondary",
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>

            <FormControlLabel
              control={
                <Switch checked={formData.trackOpens} onChange={toggleTrackOpens} size="small" />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <NotificationsActiveIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2">Track Opens</Typography>
                </Box>
              }
            />
          </Box>

          <Box>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ display: "inline-block" }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={formData.to.length === 0 || !formData.subject.trim()}
                startIcon={formData.scheduledDate ? <ScheduleSendIcon /> : undefined}
              >
                {formData.scheduledDate ? "Schedule" : "Send"}
              </Button>
            </motion.div>
          </Box>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default CustomerSendEmailModal;
